/**
 * Prefs и константы слоя номенклатурной разграфки ОИ.
 */

export const OI_NOM_SCALES = [
  25_000, 50_000, 100_000, 200_000, 500_000, 1_000_000,
] as const;

export type OiNomScale = (typeof OI_NOM_SCALES)[number];

export const OI_NOM_STORAGE_KEY = "gpml.oi.nomenclatureSheets";
export const OI_NOM_PREFS_EVENT = "gpml:oi-nomenclature-prefs";

export const OI_NOM_SOURCE = "oi-nomenclature-sheets";
export const OI_NOM_FILL_LAYER = "oi-nomenclature-sheets-fill";
export const OI_NOM_LINE_LAYER = "oi-nomenclature-sheets-line";
export const OI_NOM_LABEL_LAYER = "oi-nomenclature-sheets-label";

export const OI_NOM_HIT_SOURCE = "oi-nomenclature-hits";
export const OI_NOM_HIT_FILL = "oi-nomenclature-hits-fill";
export const OI_NOM_HIT_LINE = "oi-nomenclature-hits-line";

export type OiNomPrefs = {
  /** Включённые масштабы (ключи — строки чисел). */
  enabledScales: Record<string, boolean>;
};

export const DEFAULT_OI_NOM_PREFS: OiNomPrefs = {
  enabledScales: {},
};

function isScale(v: unknown): v is OiNomScale {
  return typeof v === "number" && (OI_NOM_SCALES as readonly number[]).includes(v);
}

export function loadOiNomPrefs(): OiNomPrefs {
  try {
    const raw = localStorage.getItem(OI_NOM_STORAGE_KEY);
    if (!raw) return { enabledScales: { ...DEFAULT_OI_NOM_PREFS.enabledScales } };
    const parsed = JSON.parse(raw) as Partial<OiNomPrefs>;
    const enabledScales: Record<string, boolean> = {};
    if (parsed.enabledScales && typeof parsed.enabledScales === "object") {
      for (const [k, on] of Object.entries(parsed.enabledScales)) {
        const n = Number(k);
        if (isScale(n) && on) enabledScales[String(n)] = true;
      }
    }
    return { enabledScales };
  } catch {
    return { enabledScales: {} };
  }
}

export function saveOiNomPrefs(prefs: OiNomPrefs): void {
  try {
    localStorage.setItem(OI_NOM_STORAGE_KEY, JSON.stringify(prefs));
    window.dispatchEvent(new CustomEvent(OI_NOM_PREFS_EVENT));
  } catch {
    /* ignore */
  }
}

export function setOiNomScaleEnabled(scale: number, enabled: boolean): OiNomPrefs {
  const prefs = loadOiNomPrefs();
  const next = { ...prefs.enabledScales };
  if (enabled) next[String(scale)] = true;
  else delete next[String(scale)];
  const out = { enabledScales: next };
  saveOiNomPrefs(out);
  return out;
}

export function formatOiNomScaleLabel(scale: number): string {
  return `1:${scale.toLocaleString("ru-RU")}`;
}

export function enabledOiNomScaleList(prefs: OiNomPrefs): number[] {
  return OI_NOM_SCALES.filter((s) => prefs.enabledScales[String(s)]);
}

/** Prefs открытой панели анализа + позиция (fixed left/top, px). */
export const OI_NOM_PANEL_STORAGE_KEY = "gpml.oi.nomenclaturePanel";

export type OiNomPanelPrefs = {
  open: boolean;
  /** null — позиция по умолчанию (правый низ, с отступом от scale bar). */
  x: number | null;
  y: number | null;
};

export const DEFAULT_OI_NOM_PANEL_PREFS: OiNomPanelPrefs = {
  open: false,
  x: null,
  y: null,
};

export function loadOiNomPanelPrefs(): OiNomPanelPrefs {
  try {
    const raw = localStorage.getItem(OI_NOM_PANEL_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_OI_NOM_PANEL_PREFS };
    const parsed = JSON.parse(raw) as Partial<OiNomPanelPrefs>;
    return {
      open: Boolean(parsed.open),
      x: typeof parsed.x === "number" && Number.isFinite(parsed.x) ? parsed.x : null,
      y: typeof parsed.y === "number" && Number.isFinite(parsed.y) ? parsed.y : null,
    };
  } catch {
    return { ...DEFAULT_OI_NOM_PANEL_PREFS };
  }
}

export function saveOiNomPanelPrefs(prefs: OiNomPanelPrefs): void {
  try {
    localStorage.setItem(OI_NOM_PANEL_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}
